import React, { useMemo, forwardRef, useContext } from "react";
import clsx from "clsx";
import fp from "lodash/fp";
import { Form, Col, FormGroup, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import PhoneInputWithCountry from "react-phone-number-input/react-hook-form";

import { ErrorMessage, Skeleton, RequiredMark } from "components";
import { BrandingColorContext } from "components/contexts";
import { useLoader } from "hooks";
import { phoneMinLength8, maxPhoneLength15 } from "validators/userValidators";
import { required } from "validators/validation";

import styles from "./fields.module.scss";

export type InputProps = {
	name: string;
	label?: string;
	isLoading?: boolean;
	isRequired?: boolean;
	disabled?: boolean;
	colLabel?: number;
	colInput?: number;
};

const validateWithRequired = {
	required,
	phoneMinLength8,
	maxPhoneLength15,
};

const validate = {
	phoneMinLength8,
	maxPhoneLength15,
};

const ControlInput = forwardRef<HTMLInputElement>((controlProps, ref) => {
	return <Form.Control ref={ref} {...controlProps} />;
});

const HookFormPhoneInput = ({
	name,
	label,
	isLoading,
	isRequired = false,
	disabled,
	colLabel = 6,
	colInput = 6,
}: InputProps) => {
	const { control, formState } = useFormContext();
	const { errors } = formState;
	const Loader = useLoader(isLoading);

	const validatePhone = useMemo(
		() => (isRequired ? validateWithRequired : validate),
		[isRequired],
	);

	const invalidClass = fp.get(name, errors) && styles.errorField;

	const brandingColor = useContext(BrandingColorContext);

	return (
		<FormGroup as={Row} controlId={name}>
			{label && (
				<Form.Label column sm={colLabel}>
					{label}
					{isRequired && <RequiredMark />}
				</Form.Label>
			)}

			<Col sm={colInput}>
				<Loader
					skeleton={<Skeleton width="100%" height={34} />}
					component={
						<PhoneInputWithCountry
							control={control}
							name={name}
							rules={{ validate: validatePhone }}
							inputComponent={ControlInput}
							className={clsx(
								brandingColor && "branding-color-container",
								invalidClass,
							)}
							defaultCountry="US"
							initialValueFormat="national"
							international={false}
							disabled={disabled}
						/>
					}
				/>
				<ErrorMessage field={name} />
			</Col>
		</FormGroup>
	);
};

export default HookFormPhoneInput;
